import {
  bindCenterOpenApiFetch,
  isFormDataBody,
  normalizeCenterOpenApiBaseUrl,
  requestHeadersToRecord,
} from "@airange/shared/center-openapi-transport";
import { type ApiRequestOptions, type SsoSessionApiClient } from "@airange/shared/oidc-session";
import { joinUrlPath } from "@airange/shared/url";
import { createAdminRuntimeConfigError } from "./admin-runtime-access-config";
import { CENTER_OPENAPI_ORIGIN_OPTIONS } from "./center-api-common";

async function fetchAdminBffApi(
  baseUrl: string,
  path: string,
  options: ApiRequestOptions,
  fetchImpl: typeof fetch,
) {
  const normalizedBaseUrl = normalizeCenterOpenApiBaseUrl(baseUrl, CENTER_OPENAPI_ORIGIN_OPTIONS);
  if (typeof window !== "undefined") {
    const baseOrigin = new URL(`${normalizedBaseUrl}/`).origin;
    if (baseOrigin !== window.location.origin) {
      throw createAdminRuntimeConfigError(
        "admin.access.bff_api_origin_mismatch",
        "Admin BFF API transport requires same-origin API URLs.",
        {
          apiOrigin: baseOrigin,
          currentOrigin: window.location.origin,
        },
      );
    }
  }
  const hasMultipartBody = isFormDataBody(options.body);
  const hasBody = options.body !== undefined;
  const headers: Record<string, string> = {};
  for (const [key, value] of Object.entries(requestHeadersToRecord(options.headers))) {
    if (key.toLowerCase() === "authorization") {
      continue;
    }
    if (hasBody && key.toLowerCase() === "content-type") {
      continue;
    }
    headers[key] = value;
  }
  if (hasBody && !hasMultipartBody) {
    headers["Content-Type"] = "application/json";
  }
  const requestInit: RequestInit = {
    method: options.method ?? "GET",
    credentials: "include",
    headers,
  };
  if (hasBody) {
    requestInit.body = hasMultipartBody ? (options.body as BodyInit) : JSON.stringify(options.body);
  }
  return await fetchImpl(joinUrlPath(normalizedBaseUrl, path), requestInit);
}

export function createCenterApiClient(
  baseUrl: string,
  fetchImpl: typeof fetch = fetch,
): SsoSessionApiClient {
  return {
    async fetchWithAuth(path: string, options: ApiRequestOptions = {}) {
      return await fetchAdminBffApi(baseUrl, path, options, fetchImpl);
    },
  };
}

export function getCenterOpenApiClientOrigin(baseUrl: string) {
  const normalizedBaseUrl = normalizeCenterOpenApiBaseUrl(baseUrl, CENTER_OPENAPI_ORIGIN_OPTIONS);
  return new URL(`${normalizedBaseUrl}/`).origin;
}

export const createCenterOpenApiFetch = bindCenterOpenApiFetch(
  createCenterApiClient,
  CENTER_OPENAPI_ORIGIN_OPTIONS,
);
